// ── Reports ("/reports") — the generated-report library (Wave 7, SECURITY) ──
// ONE honest read (the server-visibility-filtered report list) routed through
// useHonestQuery → <HonestState>. The page NEVER hand-rolls state branching: a
// gate / permission-denied / unavailable / error response renders via
// <HonestState> (tagged data-honest-posture), NEVER as a plain empty table.
//
// ── PARTICIPANT GENERATED-REPORT EXCLUSION (FR-REPORT-001, release-blocking) ──
// A `participant` is DENIED the module entirely:
//   - the route manifest's reports route is generic-not-found for direct URL +
//     has no nav for participant at the ROUTE level (reportViewRoles);
//   - THIS page ALSO fail-closes (canAccessReportsModule) so a participant who
//     somehow reached the component renders ONLY permission-denied with NO list —
//     and CRITICALLY the report read NEVER fires for a participant (`enabled`
//     gates the query). A participant must not learn a report even EXISTS.
//
// ── export ≤ view / no leak ──────────────────────────────────────────────────
// The server visibility-filters the list per role (staff=all; coach=coach_feedback
// + team_admin; cohort_lead + company_admin = team_admin; participant=none). The
// FE applies filterReadableReports (D-3) belt-and-suspenders so a backend
// regression can never leak a vjal_internal / ceo_confidential / coach_feedback
// row into a non-staff surface. The list is a READ-AND-NAVIGATE surface: every
// row links to the report viewer.

import { CollectionToolbar, IconDoc, PageFrame, ProTable } from "@vjal/ui";
import { useMemo } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import type { CoachingArtifactDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useReportsCollection } from "./reports-collection";
import { canAccessReportsModule, filterReadableReportDtos } from "./reports-helpers";

const services = getCoachingServices();

export function ReportsPage({ role, routePath }: ProductScreenContext) {
  // ── Fail-closed module guard (participant report-exclusion) ─────────────────
  // Deny BEFORE any read fires (the read is also gated below via `enabled`) so NO
  // report request is made for an excluded role. This is load-bearing: the route
  // already denies participant, but defense-in-depth at the page keeps the read
  // from firing even if the route guard were bypassed.
  const moduleDenied = !canAccessReportsModule(role);

  const query = useHonestQuery<ListResponse<CoachingArtifactDto>>(
    coachingQueryKeys.list("reports.list"),
    () => services.reports.list(),
    { enabled: !moduleDenied }
  );

  // D-3 belt-and-suspenders: drop every report type the role may not read BEFORE
  // the in-memory filter pass, so a confidential row can never surface even via
  // search. The backend server-visibility filter is authoritative; this is the FE
  // half of the §7.4 leak guarantee.
  const items = query.data?.items;
  const allRows = useMemo(() => filterReadableReportDtos(role, items ?? []), [items, role]);
  const collection = useReportsCollection(allRows, routePath);

  // ── Fail closed: a denied role gets a permission-denied posture, NO read. ───
  if (moduleDenied) {
    return (
      <section aria-label="Reports" className="col-span-12" data-testid="reports-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to reports"
            message="The reports area is for coaching staff and assigned leads. Your own coaching summary is available on your dashboard."
          />
        </div>
      </section>
    );
  }

  // useHonestQuery flags an empty {items:[]} as "empty"; once filters or the D-3
  // filter narrow a non-empty list to zero we render the empty body ourselves
  // (never a blank). Note: the visible empty must reflect the D-3-filtered
  // allRows, so a non-staff role whose only rows were confidential reads "empty".
  const filteredToEmpty = query.posture === null && collection.total === 0;
  const posture = filteredToEmpty ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  return (
    <PageFrame title="Reports" subtitle="Generated reports you're authorized to open." className="col-span-12">
      <section aria-label="Reports" className="flex flex-col gap-4" data-testid="reports-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by title, session, or cohort…"
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              icon={<IconDoc size={22} />}
              title={posture === "empty" ? "No reports yet" : undefined}
              message={
                posture === "empty"
                  ? "Reports you're authorized to see will appear here once sessions are held and reports are generated."
                  : undefined
              }
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export for reports IR. */
            <ProTable
              collection={collection}
              className="p-0"
              tableProps={{
                caption: "Reports in the current view",
                emptyTitle: "No reports match your search",
                emptyMessage: "Clear or change the filters above to widen the view.",
                tableClassName: "min-w-[760px]",
              }}
              stableChipNames
            />
          )}
        </div>
      </section>
    </PageFrame>
  );
}
